/**
 * The frame every admin screen sits in.
 *
 * Before this, each of the five screens drew its own heading and its own
 * assortment of "← back" links, so where you were and what else existed had to
 * be worked out from whichever one you happened to be on. There is one header
 * now: the retreat you are working in, the three places inside it, and the
 * three that belong to no retreat.
 *
 * It renders nothing clever. The value is that it renders the same thing every
 * time, so the admin screens stop being five separate pages.
 */

import { text } from '../js/html.js'

/** Where you can be inside a retreat. */
export const RETREAT_TABS = [
  { key: 'story', label: 'Story', path: (id) => `/admin/retreat/${id}` },
  { key: 'questions', label: 'Questions', path: (id) => `/admin/retreat/${id}/questions` },
  { key: 'gallery', label: 'Gallery', path: (id) => `/admin/retreat/${id}/gallery` },
  { key: 'settings', label: 'Settings', path: (id) => `/admin/retreat/${id}/settings` },
]

/** Where you can be outside one. */
export const GLOBAL_TABS = [
  { key: 'retreats', label: 'Retreats', path: () => '/admin' },
  { key: 'delivery', label: 'Delivery', path: () => '/admin/delivery' },
  { key: 'system', label: 'System', path: () => '/admin/system' },
]

/**
 * @param {{
 *   retreat?: {id: number, name: string, starts_on?: string, ends_on?: string,
 *              timezone?: string, status?: string}|null,
 *   tab: string,
 *   subtitle?: string|null,
 * }} options
 */
export function adminHeader({ retreat = null, tab, subtitle = null }) {
  return `
    <header class="ahead">
      <div class="ahead__top">
        <span class="ahead__mark">DIFFER LX</span>
        <nav class="ahead__global">
          ${GLOBAL_TABS.map((item) => link(item, item.key === tab, null)).join('')}
          <button class="link-button" data-admin-exit>Exit to app</button>
        </nav>
      </div>

      ${
        retreat === null
          ? `<h1 class="ahead__title">${text(labelFor(tab))}</h1>
             ${subtitle === null ? '' : `<p class="ahead__meta">${text(subtitle)}</p>`}`
          : `<div class="ahead__retreat">
               <h1 class="ahead__title">
                 ${text(retreat.name)}
                 ${retreat.status ? `<span class="pill pill--${text(retreat.status)}">${text(retreat.status)}</span>` : ''}
               </h1>
               <p class="ahead__meta">
                 ${text(retreat.starts_on ?? '')} → ${text(retreat.ends_on ?? '')}
                 ${retreat.timezone ? ` · ${text(retreat.timezone)}` : ''}
                 ${subtitle === null ? '' : ` · ${text(subtitle)}`}
               </p>
             </div>
             <nav class="ahead__tabs">
               ${RETREAT_TABS.map((item) => link(item, item.key === tab, retreat.id)).join('')}
             </nav>`
      }
    </header>
  `
}

function link(item, isHere, retreatId) {
  return `
    <a class="ahead__tab ${isHere ? 'ahead__tab--here' : ''}"
       href="${text(item.path(retreatId))}"
       ${isHere ? 'aria-current="page"' : ''}
       data-admin-link>${text(item.label)}</a>
  `
}

function labelFor(tab) {
  return GLOBAL_TABS.find((item) => item.key === tab)?.label ?? 'Admin'
}

/**
 * Turns the header's links into navigation.
 *
 * The admin lives inside the same router as everything else, but its anchors
 * are not `data-link` — the router's global handler would fire before the
 * screen had a chance to warn about unsaved work. One handler here keeps that
 * decision in one place.
 */
export function mountAdminHeader(root, { onNavigate, onExit }) {
  root.querySelectorAll('[data-admin-link]').forEach((anchor) =>
    anchor.addEventListener('click', (event) => {
      event.preventDefault()
      onNavigate(anchor.getAttribute('href'))
    }),
  )

  root.querySelector('[data-admin-exit]')?.addEventListener('click', () => onExit())
}
